import React, { useEffect, useRef, useState } from 'react';
import { usePwaInstall } from '~/hooks/usePwaInstall';
import styles from './PwaInstallPrompt.module.css';

const AUTO_HIDE_MS = 7000;
const APP_ICON = `${process.env.PUBLIC_URL}/android-chrome-192x192.png`;

type Phase = 'banner' | 'fab';
type HelpPlatform = 'ios' | 'android' | 'desktop' | null;

/**
 * Dymek zachęcający do instalacji PWA — pokazywany na stronie głównej przy KAŻDYM wejściu,
 * o ile aplikacja nie jest już zainstalowana (niezależnie od zdarzenia beforeinstallprompt).
 * - X lub upływ 7 s chowa dymek, ale zostaje trwały mały przycisk (FAB) w rogu.
 * - Gdy natywny prompt jest dostępny (Android/Chrome) → instalacja jednym kliknięciem.
 * - Gdy niedostępny (iOS, lub Android zanim zdarzenie wystrzeli) → instrukcja krok-po-kroku.
 */
const PwaInstallPrompt: React.FC = () => {
  const { canInstall, canPromptAndroid, isIOS, isIOSSafari, isAndroid, promptInstall } =
    usePwaInstall();
  const [phase, setPhase] = useState<Phase>('banner');
  const [help, setHelp] = useState<HelpPlatform>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-ukrycie dymka po 7 s → zwijamy do FAB. Timer startuje, gdy dymek jest widoczny
  // (faza 'banner', instalacja możliwa, brak otwartej instrukcji).
  useEffect(() => {
    if (phase !== 'banner' || !canInstall || help) return;
    timerRef.current = setTimeout(() => setPhase('fab'), AUTO_HIDE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [phase, canInstall, help]);

  // Aplikacja już zainstalowana (uruchomiona jako PWA) — nic nie pokazujemy.
  if (!canInstall) return null;

  const handleInstallClick = async () => {
    // 1) Natywny prompt dostępny (Android/Chrome) → instalacja jednym kliknięciem.
    if (canPromptAndroid) {
      const outcome = await promptInstall();
      if (outcome !== 'accepted') setPhase('fab'); // odrzucono → zostaje FAB
      return;
    }
    // 2) Brak natywnego promptu → instrukcja dopasowana do platformy (jak na iPhone).
    setHelp(isIOS ? 'ios' : isAndroid ? 'android' : 'desktop');
  };

  const dismissToFab = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setPhase('fab');
  };

  return (
    <>
      {phase === 'banner' && (
        <div className={styles.banner} role="dialog" aria-label="Zainstaluj aplikację Czateria24">
          <img src={APP_ICON} alt="" className={styles.icon} aria-hidden="true" />
          <div className={styles.text}>
            <span className={styles.title}>Zainstaluj Czateria24</span>
            <span className={styles.subtitle}>Dodaj na ekran główny — działa jak zwykła apka.</span>
          </div>
          <button type="button" className={styles.installBtn} onClick={handleInstallClick}>
            Zainstaluj
          </button>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={dismissToFab}
            aria-label="Zamknij"
          >
            ✕
          </button>
        </div>
      )}

      {phase === 'fab' && (
        <button
          type="button"
          className={styles.fab}
          onClick={handleInstallClick}
          aria-label="Zainstaluj aplikację Czateria24"
          title="Zainstaluj aplikację"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              fill="currentColor"
              d="M12 3a1 1 0 0 1 1 1v8.59l2.3-2.3a1 1 0 1 1 1.4 1.42l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 1 1 1.4-1.42l2.3 2.3V4a1 1 0 0 1 1-1Zm-7 14a1 1 0 0 1 1 1v1h12v-1a1 1 0 1 1 2 0v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1Z"
            />
          </svg>
          <span className={styles.fabLabel}>Instaluj</span>
        </button>
      )}

      {help && (
        <div className={styles.iosOverlay} onClick={() => setHelp(null)}>
          <div
            className={styles.iosCard}
            role="dialog"
            aria-label="Jak zainstalować aplikację"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={styles.iosClose}
              onClick={() => setHelp(null)}
              aria-label="Zamknij"
            >
              ✕
            </button>
            <img src={APP_ICON} alt="" className={styles.iosIcon} aria-hidden="true" />
            <h3 className={styles.iosTitle}>Zainstaluj Czateria24</h3>

            {help === 'ios' &&
              (isIOSSafari ? (
                <ol className={styles.iosSteps}>
                  <li>
                    Dotknij ikony <strong>Udostępnij</strong>{' '}
                    <span className={styles.shareGlyph} aria-hidden="true">⬆️</span> na dole ekranu.
                  </li>
                  <li>
                    Wybierz <strong>„Do ekranu początkowego"</strong>.
                  </li>
                  <li>
                    Potwierdź przyciskiem <strong>„Dodaj"</strong>.
                  </li>
                </ol>
              ) : (
                <p className={styles.iosNote}>
                  Otwórz tę stronę w przeglądarce <strong>Safari</strong>, a następnie użyj
                  opcji <strong>Udostępnij → „Do ekranu początkowego"</strong>. Inne przeglądarki
                  na iPhone nie pozwalają instalować aplikacji.
                </p>
              ))}

            {help === 'android' && (
              <ol className={styles.iosSteps}>
                <li>
                  Dotknij menu <strong>⋮</strong> (trzy kropki) w prawym górnym rogu przeglądarki.
                </li>
                <li>
                  Wybierz <strong>„Zainstaluj aplikację"</strong> lub{' '}
                  <strong>„Dodaj do ekranu głównego"</strong>.
                </li>
                <li>
                  Potwierdź przyciskiem <strong>„Zainstaluj"</strong>.
                </li>
              </ol>
            )}

            {help === 'desktop' && (
              <ol className={styles.iosSteps}>
                <li>
                  Kliknij ikonę instalacji <strong>⊕</strong> po prawej stronie paska adresu.
                </li>
                <li>
                  Lub otwórz menu <strong>⋮</strong> → <strong>„Zainstaluj Czateria24…"</strong>.
                </li>
                <li>
                  Potwierdź przyciskiem <strong>„Zainstaluj"</strong>.
                </li>
              </ol>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PwaInstallPrompt;
